import { useOrganization, useUser } from '@clerk/react';
import { useNavigate } from '@tanstack/react-router';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  fullSeatsMessage,
  SEAT_LIMIT_CODES,
} from '@/components/seats/full-seats-message';
import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { pricingMoney } from '@/containers/settings/billing/pricing-v4';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { bestEffortAnalytics } from '@/lib/best-effort-analytics';
import { formatShortDate } from '@/lib/card-trial';
import { iconCircleInfo, iconSpinnerThird } from '@/lib/icons';
import { useGetPlansQuery } from '@/store/features/billing.feature';

import {
  clearTeamInvitePrompt,
  teamInvitePromptPending,
} from './team-invite-prompt';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ClerkErrors = {
  errors?: { code: string; message: string; longMessage?: string }[];
};

/**
 * Invite your team, once, as onboarding hands the workspace's creator to the
 * app: one email row per open seat. A trial says what it bills when it ends;
 * Free says its seats cost nothing. More seats than the plan holds need a
 * paid plan, so the pop-up points there.
 */
export function TeamInvitePrompt() {
  const { user } = useUser();
  const { organization, membership } = useOrganization();
  // Clerk's org role: inviting needs it, so no staff fallback here.
  const isAdmin = membership?.role === 'org:admin';
  // A render gate only: a flag still loading must not clear the marker.
  const enabled = useFeatureFlagEnabled('team_invite_prompt_v1') === true;
  const { trial, isReady, isAppSumo } = useFeatureAccess();
  const { currentData: plans } = useGetPlansQuery(
    { organizationId: organization?.id ?? '' },
    { skip: !organization?.id }
  );
  const userId = user?.id;
  const [pending, setPending] = useState(false);
  useEffect(() => {
    setPending(teamInvitePromptPending(userId));
  }, [userId]);

  const onTrial = trial?.active === true && trial.started_at != null;
  const onFree =
    !onTrial &&
    !isAppSumo &&
    plans?.current_plan?.pricing_version === 'v4' &&
    plans.current_plan.tier_key === 'free';
  const seats = onTrial ? trial?.seat_limit : plans?.base_seats;
  // A pending invitation holds a seat too, as Clerk's member cap counts it.
  const used = organization
    ? (organization.membersCount || 1) +
      (organization.pendingInvitationsCount || 0)
    : null;
  // The role decides eligibility, so wait for it: an unloaded membership
  // isn't a member who can't invite.
  const loaded = isReady && plans != null && used != null && membership != null;
  const eligible =
    isAdmin && (onTrial || onFree) && seats != null && seats > (used ?? 0);

  // Only the pop-up's first chance counts: a paid, AppSumo or full workspace
  // never sees it, and doesn't later either.
  useEffect(() => {
    if (pending && loaded && !eligible) {
      clearTeamInvitePrompt();
      setPending(false);
    }
  }, [pending, loaded, eligible]);

  if (
    !enabled ||
    !pending ||
    !loaded ||
    !eligible ||
    seats == null ||
    used == null
  ) {
    return null;
  }

  const renewal = onTrial ? trial?.renews_to : null;
  const renewalPlan = plans?.plans.find(
    (plan) => plan.pricing_version === 'v4' && plan.tier_key === renewal?.plan
  );
  const yearly = renewal?.interval === 'year';
  const seatPrice = (
    yearly ? renewalPlan?.yearly_price : renewalPlan?.monthly_price
  )?.price;

  return (
    <TeamInviteDialog
      seats={seats}
      used={used}
      plan={onTrial ? 'trial' : 'free'}
      billing={
        renewal && trial?.ends_at
          ? {
              endsOn: formatShortDate(trial.ends_at),
              planLabel: renewal.label,
              seatPrice:
                seatPrice == null
                  ? null
                  : `${pricingMoney(seatPrice)} per seat a ${yearly ? 'year' : 'month'}`,
            }
          : null
      }
      onClosed={() => {
        clearTeamInvitePrompt();
        setPending(false);
      }}
    />
  );
}

function TeamInviteDialog({
  seats,
  used,
  plan,
  billing,
  onClosed,
}: {
  seats: number;
  used: number;
  plan: 'trial' | 'free';
  /** A card trial's renewal: what it bills at its end. */
  billing: {
    endsOn: string;
    planLabel: string;
    seatPrice: string | null;
  } | null;
  onClosed: () => void;
}) {
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [open, setOpen] = useState(true);
  const [emails, setEmails] = useState(() =>
    Array.from({ length: seats - used }, () => '')
  );
  const [errors, setErrors] = useState<(string | null)[]>([]);
  const [full, setFull] = useState(false);
  const [sending, setSending] = useState(false);
  const onTrial = plan === 'trial';
  const limit = fullSeatsMessage(seats, onTrial);

  useEffect(() => {
    bestEffortAnalytics(() =>
      posthog?.capture('team_invite_prompt_viewed', { plan, seats, used })
    );
  }, [posthog, plan, seats, used]);

  const close = (reason: 'sent' | 'skipped' | 'plans') => {
    bestEffortAnalytics(() =>
      posthog?.capture('team_invite_prompt_closed', { plan, reason })
    );
    setOpen(false);
    onClosed();
  };

  const seePlans = () => {
    close('plans');
    void navigate({
      to: '/plans',
      search: {
        status: undefined,
        credits_added: undefined,
        plan_name: undefined,
      },
    });
  };

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!organization || sending) return;
    const typed = emails.map((email) => email.trim().toLowerCase());
    const next = typed.map((email, i): string | null =>
      !email
        ? null
        : !EMAIL.test(email)
          ? 'Enter a valid email.'
          : typed.indexOf(email) !== i
            ? 'This email is already above.'
            : null
    );
    if (!typed.some(Boolean)) next[0] = 'Enter an email to invite.';
    setErrors(next);
    if (next.some(Boolean)) return;

    setSending(true);
    const failed: (string | null)[] = typed.map(() => null);
    let sent = 0;
    for (const [i, email] of typed.entries()) {
      if (!email) continue;
      try {
        await organization.inviteMember({
          emailAddress: email,
          role: 'org:member',
        });
        sent += 1;
      } catch (error) {
        const found = (error as ClerkErrors).errors ?? [];
        if (found.some((e) => SEAT_LIMIT_CODES.has(e.code))) setFull(true);
        failed[i] =
          found[0]?.longMessage ??
          found[0]?.message ??
          'Couldn’t send this invitation. Try again.';
      }
    }
    setSending(false);
    if (sent > 0) {
      bestEffortAnalytics(() =>
        posthog?.capture('team_invite_prompt_sent', { plan, invites: sent })
      );
      toast.success(
        sent === 1 ? 'Invitation sent' : `${sent} invitations sent`
      );
    }
    if (!failed.some(Boolean)) {
      close('sent');
      return;
    }
    // Sent rows empty out; the ones that failed keep their email and error.
    setEmails(emails.map((email, i) => (failed[i] ? email : '')));
    setErrors(failed);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !sending) close('skipped');
      }}
    >
      <DialogContent className='max-w-[480px] gap-0 overflow-hidden rounded-2xl p-0'>
        <form onSubmit={send} noValidate>
          <div className='px-7 pt-8'>
            <DialogTitle className='text-2xl font-semibold tracking-[-0.02em]'>
              Invite your team
            </DialogTitle>
            <p className='mt-1 text-sm text-muted-foreground'>
              {used} of {seats} seats used
            </p>
          </div>

          <div className='px-7 pt-5'>
            <div className='space-y-2.5'>
              {emails.map((email, i) => (
                <div key={i}>
                  <Input
                    type='email'
                    aria-label={`Teammate ${i + 1} email`}
                    placeholder='teammate@company.com'
                    value={email}
                    disabled={sending}
                    autoFocus={i === 0}
                    aria-invalid={errors[i] ? true : undefined}
                    className='h-11 text-[15px]'
                    onChange={(e) => {
                      const value = e.target.value;
                      setEmails((rows) =>
                        rows.map((row, j) => (j === i ? value : row))
                      );
                      setErrors((rows) =>
                        rows.map((row, j) => (j === i ? null : row))
                      );
                    }}
                  />
                  {errors[i] && (
                    <p role='alert' className='mt-1 text-xs text-destructive'>
                      {errors[i]}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {full ? (
              <div
                role='alert'
                className='mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-[13px]'
              >
                <p className='font-semibold text-foreground'>{limit.title}</p>
                <p className='mt-0.5 text-muted-foreground'>
                  {limit.description}
                </p>
              </div>
            ) : (
              <DialogDescription className='mt-4 flex gap-2.5 rounded-xl bg-muted/60 px-4 py-3 text-[13px] leading-5 text-muted-foreground'>
                <AppIcon
                  icon={iconCircleInfo}
                  className='mt-0.5 h-3.5 w-3.5 shrink-0'
                />
                <span>
                  {billing
                    ? `You're only billed for the seats you have when your trial ends on ${billing.endsOn}.${
                        billing.seatPrice
                          ? ` ${billing.planLabel} is ${billing.seatPrice}.`
                          : ''
                      }`
                    : onTrial
                      ? `Your trial includes ${seats} seats.`
                      : `Free includes ${seats} seats at no cost.`}
                </span>
              </DialogDescription>
            )}

            <p className='mt-3 text-[13px] text-muted-foreground'>
              Need more than {seats} seats?{' '}
              <button
                type='button'
                onClick={seePlans}
                disabled={sending}
                className='font-semibold text-primary underline-offset-4 hover:underline disabled:opacity-50'
              >
                {onTrial ? 'Start your paid plan' : 'Upgrade to a paid plan'}
              </button>
            </p>
          </div>

          <div className='px-7 pt-6 pb-7'>
            <Button
              type='submit'
              className='h-12 w-full text-[15px]'
              disabled={sending}
            >
              {sending && <AppIcon icon={iconSpinnerThird} spin />}
              Send invites
            </Button>
            <button
              type='button'
              onClick={() => close('skipped')}
              disabled={sending}
              className='mt-2 w-full py-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50'
            >
              Skip for now
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
